import * as React from "react";
import { AlertTriangle, CheckCircle2, MinusCircle, XCircle } from "lucide-react";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";

/** Visual tone of a status value; one vocabulary for every OK/NOK in the app. */
export type StatusTone = "ok" | "nok" | "warn" | "info" | "neutral";

export function statusTone(status: string | boolean | null | undefined): StatusTone {
  if (status === true) return "ok";
  if (status === false) return "nok";
  const value = (status ?? "").toString().trim().toLowerCase();
  if (["ok", "pass", "pizza", "found", "removed"].includes(value)) return "ok";
  if (["nok", "fail", "no pizza", "missing", "detected", "burnt", "invalid photo"].includes(value))
    return "nok";
  if (["warning", "warn", "uncooked", "non_detectable", "borderline"].includes(value)) return "warn";
  if (["info"].includes(value)) return "info";
  return "neutral";
}

/**
 * Colour is for problems: a NOK is a filled red pill, everything else is an outline at most, so that a
 * screen full of passed checks stays calm and the failure is what catches the eye.
 */
export const TONE_BADGE: Record<StatusTone, string> = {
  ok: "border-green-500/25 bg-transparent text-green-400/90",
  nok: "border-red-500/35 bg-red-500/15 text-red-300",
  warn: "border-amber-500/30 bg-transparent text-amber-300/90",
  info: "border-border bg-transparent text-muted-foreground",
  neutral: "border-border bg-transparent text-muted-foreground",
};

/** Solid fill of the tone (dots). */
export const TONE_SOLID: Record<StatusTone, string> = {
  ok: "bg-green-500/70",
  nok: "bg-red-500",
  warn: "bg-amber-400",
  info: "bg-muted-foreground/50",
  neutral: "bg-muted-foreground/40",
};

/** Text colour of the tone (icons, inline values). */
export const TONE_TEXT: Record<StatusTone, string> = {
  ok: "text-green-400",
  nok: "text-red-400",
  warn: "text-amber-400",
  info: "text-muted-foreground",
  neutral: "text-muted-foreground",
};

const SIZES = {
  xs: "h-5 px-1.5 text-[10px]",
  sm: "h-6 px-2 text-xs",
  md: "h-7 px-2.5 text-xs",
};

interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** the raw status (OK / NOK / WARNING / ...); also the label unless `label` is given */
  status: string | boolean | null | undefined;
  label?: React.ReactNode;
  /** overrides the tone derived from `status` */
  tone?: StatusTone;
  size?: keyof typeof SIZES;
  icon?: boolean;
  /** a passed / neutral status as plain muted text (tables with many status columns) */
  quiet?: boolean;
}

/** Compact OK/NOK pill used by the tables, the detail panels and the order tiles. */
export function StatusBadge({
  status,
  label,
  tone,
  size = "sm",
  icon = false,
  quiet = false,
  className,
  ...props
}: StatusBadgeProps) {
  const { t } = useLocalization();
  const resolved = tone ?? statusTone(status);
  // the raw OK / NOK in the language of the UI (NEOK in Czech), other values as they are
  const statusText =
    status === null || status === undefined || status === ""
      ? t("status.na")
      : status === "OK"
        ? t("status.ok")
        : status === "NOK"
          ? t("status.nok")
          : String(status);
  const text = label ?? statusText;
  if (quiet && resolved !== "nok" && resolved !== "warn") {
    return (
      <span
        className={cn("whitespace-nowrap text-xs font-medium text-muted-foreground/80", className)}
        {...props}
      >
        {text}
      </span>
    );
  }
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border font-semibold uppercase leading-none tracking-wide",
        SIZES[size],
        TONE_BADGE[resolved],
        className,
      )}
      {...props}
    >
      {icon && <StatusIcon tone={resolved} className="h-3 w-3" />}
      {text}
    </span>
  );
}

export function StatusIcon({ tone, className }: { tone: StatusTone; className?: string }) {
  const Icon =
    tone === "ok" ? CheckCircle2 : tone === "nok" ? XCircle : tone === "warn" ? AlertTriangle : MinusCircle;
  return <Icon className={cn("h-4 w-4 shrink-0", TONE_TEXT[tone], className)} aria-hidden />;
}

export function StatusDot({ tone, className, title }: { tone: StatusTone; className?: string; title?: string }) {
  return (
    <span
      title={title}
      className={cn("inline-block h-2 w-2 shrink-0 rounded-full", TONE_SOLID[tone], className)}
    />
  );
}
